/**
 * Prompts the user to claim their prior unauthenticated chat history when a
 * just-linked identity already has channel-user messages on file.
 */

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  externalIdentityService,
  type OrphanInfo,
} from "@/services/external-identity-service";

interface MergePriorHistoryDialogProps {
  identityId: string;
  orphan: OrphanInfo;
  onDone: () => void;
}

export function MergePriorHistoryDialog({
  identityId,
  orphan,
  onDone,
}: MergePriorHistoryDialogProps) {
  const [busy, setBusy] = useState(false);

  const handleMerge = async () => {
    setBusy(true);
    try {
      await externalIdentityService.mergePriorHistory(identityId);
      toast.success("Prior history moved to your account");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not move history");
    } finally {
      setBusy(false);
      onDone();
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onDone()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>We found prior messages</DialogTitle>
          <DialogDescription>
            This account has {orphan.chatCount} chat{orphan.chatCount === 1 ? "" : "s"} (
            {orphan.messageCount} message{orphan.messageCount === 1 ? "" : "s"}) from before
            you linked. Move them to your TeamMate account?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onDone} disabled={busy}>
            Keep separate
          </Button>
          <Button onClick={handleMerge} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Move to my account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
